import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Divider,
  Chip,
  InputAdornment,
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
  Download as DownloadIcon,
  Email as EmailIcon,
  Preview as PreviewIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Language as LanguageIcon,
  AttachMoney as CurrencyIcon,
  CalendarToday as DateIcon,
  Numbers as NumberIcon,
} from '@mui/icons-material';
import {
  useInvoices,
  useDownloadInvoicePDF,
  useSendInvoiceEmail,
} from '../../hooks/useBilling';
import {
  SUPPORTED_TEMPLATES,
  TEMPLATE_TO_LABEL,
  getTemplateConfig,
  processLogoUpload,
  getLogoUploadHelperText,
  SUPPORTED_LANGUAGES,
  LANGUAGE_TO_LABEL,
  getInvoiceNumberLabel,
  getVatNumberLabel,
  SUPPORTED_CURRENCIES,
  getCurrencySymbol,
  getCurrencyLabel,
  SUPPORTED_DATE_FORMATS,
  getDateFormatPreview,
  generateInvoiceNumber,
  validateInvoiceNumber,
  getTodayDate,
  getLastDayOfCurrentMonth,
  calculatePaymentDueDate,
  getTemplateChangeUpdates,
  getLanguageChangeUpdates,
  getGeneralFieldLabels,
  createDefaultGeneralValues,
} from '../../utils/invoice-pdf-general-section-utilities';

const InvoicePDFGenerator = ({ invoiceId, onClose, open }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const { data: invoicesData } = useInvoices();
  const downloadPDFMutation = useDownloadInvoicePDF();
  const sendEmailMutation = useSendInvoiceEmail();

  const defaultValues = useMemo(() => createDefaultGeneralValues(), []);

  const [pdfSettings, setPdfSettings] = useState({
    template: defaultValues.template,
    language: defaultValues.language,
    currency: defaultValues.currency,
    dateFormat: defaultValues.dateFormat,
    logo: defaultValues.logo,
    invoiceNumber: defaultValues.invoiceNumberObject.value,
    invoiceNumberLabel: defaultValues.invoiceNumberObject.label,
    dateOfIssue: defaultValues.dateOfIssue,
    dateOfService: defaultValues.dateOfService,
    paymentDue: defaultValues.paymentDue,
    taxLabelText: defaultValues.taxLabelText,
    sellerVatLabel: getVatNumberLabel('en'),
    buyerVatLabel: getVatNumberLabel('en'),
  });

  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  const invoices = useMemo(() => invoicesData?.items || invoicesData || [], [invoicesData]);
  const selectedInvoice = useMemo(() => 
    invoices.find(inv => inv.id === invoiceId), 
    [invoices, invoiceId]
  );

    const fieldLabels = useMemo(() =>
    getGeneralFieldLabels(pdfSettings.language), 
    [pdfSettings.language]
  );

  const templateConfig = useMemo(() => 
    getTemplateConfig(pdfSettings.template), 
    [pdfSettings.template]
  );

  const handleTemplateChange = useCallback((newTemplate) => {
    const updates = getTemplateChangeUpdates(newTemplate, pdfSettings);
    setPdfSettings(prev => ({
      ...prev,
      template: newTemplate,
      ...updates,
    }));
  }, [pdfSettings]);

  const handleLanguageChange = useCallback((newLanguage) => {
    const updates = getLanguageChangeUpdates(newLanguage, pdfSettings.invoiceNumber);
    setPdfSettings(prev => ({
      ...prev,
      language: newLanguage,
      invoiceNumberLabel: updates['invoiceNumberObject.label'],
      sellerVatLabel: updates['seller.vatNoLabelText'],
      buyerVatLabel: updates['buyer.vatNoLabelText'],
      taxLabelText: updates.taxLabelText,
    }));
  }, [pdfSettings.invoiceNumber]);

  const handleLogoUpload = useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const result = await processLogoUpload(file);
    if (result.success) {
      setPdfSettings(prev => ({ ...prev, logo: result.data }));
      enqueueSnackbar(t('billing.pdf.logoUploaded', 'Logo uploaded successfully'), { variant: 'success' });
    } else {
      enqueueSnackbar(result.error, { variant: 'error' });
    }
  }, [enqueueSnackbar, t]);

  const handleRemoveLogo = useCallback(() => {
    setPdfSettings(prev => ({ ...prev, logo: '' }));
  }, []);

  const handleDateOfIssueChange = useCallback((newDate) => {
    const newPaymentDue = calculatePaymentDueDate(newDate, 14);
    setPdfSettings(prev => ({
      ...prev,
      dateOfIssue: newDate,
      paymentDue: newPaymentDue,
    }));
  }, []);

  const handleGeneratePDF = useCallback(async () => {
    if (!invoiceId) {
      enqueueSnackbar(t('billing.pdf.noInvoiceSelected', 'Please select an invoice'), { variant: 'warning' });
      return;
    }

    const validation = validateInvoiceNumber(pdfSettings.invoiceNumber);
    if (!validation.isValid) {
      enqueueSnackbar(validation.error, { variant: 'error' });
      return;
    }

    setIsGenerating(true);
    try {
      const blob = await downloadPDFMutation.mutateAsync({
        invoiceId,
        settings: pdfSettings,
      });
      
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${pdfSettings.invoiceNumber || invoiceId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      enqueueSnackbar(t('billing.pdf.downloadSuccess', 'PDF downloaded successfully'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(t('billing.pdf.downloadError', 'Failed to download PDF'), { variant: 'error' });
    } finally {
      setIsGenerating(false);
    }
  }, [invoiceId, pdfSettings, downloadPDFMutation, enqueueSnackbar, t]);

  const handlePreviewPDF = useCallback(async () => {
    if (!invoiceId) {
      enqueueSnackbar(t('billing.pdf.noInvoiceSelected', 'Please select an invoice'), { variant: 'warning' });
      return;
    }

    setIsGenerating(true);
    try {
      const blob = await downloadPDFMutation.mutateAsync({
        invoiceId,
        settings: pdfSettings,
      });
      
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      setPreviewUrl(url);
    } catch (error) {
      enqueueSnackbar(t('billing.pdf.previewError', 'Failed to preview PDF'), { variant: 'error' });
    } finally {
      setIsGenerating(false);
    }
  }, [invoiceId, pdfSettings, downloadPDFMutation, enqueueSnackbar, t]);

  const handleSendEmail = useCallback(async () => {
    if (!invoiceId || !emailRecipients.trim()) {
      enqueueSnackbar(t('billing.pdf.emailRequired', 'Please enter email recipients'), { variant: 'warning' });
      return;
    }

    try {
      const recipients = emailRecipients.split(',').map(e => e.trim()).filter(Boolean);
      await sendEmailMutation.mutateAsync({ 
        invoiceId, 
        recipients,
        settings: pdfSettings,
      });
      
      setEmailDialogOpen(false);
      setEmailRecipients('');
      enqueueSnackbar(t('billing.pdf.emailSent', 'Invoice email sent successfully'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(t('billing.pdf.emailError', 'Failed to send email'), { variant: 'error' });
    }
  }, [invoiceId, emailRecipients, pdfSettings, sendEmailMutation, enqueueSnackbar, t]);

  const handleGenerateInvoiceNumber = useCallback(() => {
    const newNumber = generateInvoiceNumber(1);
    const newLabel = getInvoiceNumberLabel(pdfSettings.language);
    setPdfSettings(prev => ({
      ...prev,
      invoiceNumber: newNumber,
      invoiceNumberLabel: `${newLabel}:`,
    }));
  }, [pdfSettings.language]);

  const handleSetTodayDate = useCallback(() => {
    const today = getTodayDate();
    handleDateOfIssueChange(today);
  }, [handleDateOfIssueChange]);

  const handleSetEndOfMonth = useCallback(() => {
    const lastDay = getLastDayOfCurrentMonth();
    setPdfSettings(prev => ({ ...prev, dateOfService: lastDay }));
  }, []);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <PdfIcon color="primary" />
          <Typography variant="h6">
            {t('billing.pdf.generator.title', 'Invoice PDF Generator')}
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        <Stack spacing={3}>
          {selectedInvoice && (
            <Alert severity="info">
              {t('billing.pdf.selectedInvoice', 'Selected Invoice')}: {selectedInvoice.invoice_number || `INV-${selectedInvoice.id}`} - {selectedInvoice.customer_name || 'Unknown Customer'}
            </Alert>
          )}

          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                <LanguageIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                {t('billing.pdf.templateSettings', 'Template & Language Settings')}
              </Typography>
              
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{fieldLabels.template}</InputLabel>
                    <Select
                      value={pdfSettings.template}
                      label={fieldLabels.template}
                      onChange={(e) => handleTemplateChange(e.target.value)}
                    >
                      {SUPPORTED_TEMPLATES.map(template => (
                        <MenuItem key={template} value={template}>
                          {TEMPLATE_TO_LABEL[template]}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{fieldLabels.language}</InputLabel>
                    <Select
                      value={pdfSettings.language}
                      label={fieldLabels.language}
                      onChange={(e) => handleLanguageChange(e.target.value)}
                    >
                      {SUPPORTED_LANGUAGES.map(lang => (
                        <MenuItem key={lang} value={lang}>
                          {LANGUAGE_TO_LABEL[lang]}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {templateConfig.supportsLogo && (
                  <Grid item xs={12}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {fieldLabels.logo}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={2}>
                        {pdfSettings.logo ? (
                          <Box display="flex" alignItems="center" gap={1}>
                            <img 
                              src={pdfSettings.logo} 
                              alt="Logo" 
                              style={{ maxHeight: 60, maxWidth: 120, objectFit: 'contain' }} 
                            />
                            <IconButton size="small" color="error" onClick={handleRemoveLogo}>
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        ) : (
                          <Button
                            variant="outlined"
                            component="label"
                            startIcon={<UploadIcon />}
                            size="small"
                          >
                            {t('billing.pdf.uploadLogo', 'Upload Logo')}
                            <input
                              type="file"
                              hidden
                              accept="image/jpeg,image/png,image/webp"
                              onChange={handleLogoUpload}
                            />
                          </Button>
                        )}
                        <Typography variant="caption" color="text.secondary">
                          {getLogoUploadHelperText()}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                <CurrencyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                {t('billing.pdf.currencyDateSettings', 'Currency & Date Format')}
              </Typography>
              
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{fieldLabels.currency}</InputLabel>
                    <Select
                      value={pdfSettings.currency}
                      label={fieldLabels.currency}
                      onChange={(e) => setPdfSettings(prev => ({ ...prev, currency: e.target.value }))}
                    >
                      {SUPPORTED_CURRENCIES.map(currency => (
                        <MenuItem key={currency} value={currency}>
                          {currency} {getCurrencySymbol(currency)} - {getCurrencyLabel(currency)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{fieldLabels.dateFormat}</InputLabel>
                    <Select
                      value={pdfSettings.dateFormat}
                      label={fieldLabels.dateFormat}
                      onChange={(e) => setPdfSettings(prev => ({ ...prev, dateFormat: e.target.value }))}
                    >
                      {SUPPORTED_DATE_FORMATS.map(format => (
                        <MenuItem key={format} value={format}>
                          {format} ({getDateFormatPreview(format)})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                <NumberIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                {t('billing.pdf.invoiceDetails', 'Invoice Details')}
              </Typography>
              
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label={fieldLabels.invoiceNumberLabel}
                    value={pdfSettings.invoiceNumberLabel}
                    onChange={(e) => setPdfSettings(prev => ({ ...prev, invoiceNumberLabel: e.target.value }))}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label={fieldLabels.invoiceNumberValue}
                    value={pdfSettings.invoiceNumber}
                    onChange={(e) => setPdfSettings(prev => ({ ...prev, invoiceNumber: e.target.value }))}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title={t('billing.pdf.generateNumber', 'Generate Invoice Number')}>
                            <IconButton size="small" onClick={handleGenerateInvoiceNumber}>
                              <NumberIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                <DateIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                {t('billing.pdf.dates', 'Dates')}
              </Typography>
              
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label={fieldLabels.dateOfIssue}
                    value={pdfSettings.dateOfIssue}
                    onChange={(e) => handleDateOfIssueChange(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title={t('billing.pdf.setToday', 'Set to Today')}>
                            <IconButton size="small" onClick={handleSetTodayDate}>
                              <DateIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label={fieldLabels.dateOfService}
                    value={pdfSettings.dateOfService}
                    onChange={(e) => setPdfSettings(prev => ({ ...prev, dateOfService: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title={t('billing.pdf.setEndOfMonth', 'Set to End of Month')}>
                            <IconButton size="small" onClick={handleSetEndOfMonth}>
                              <DateIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label={fieldLabels.paymentDue}
                    value={pdfSettings.paymentDue}
                    onChange={(e) => setPdfSettings(prev => ({ ...prev, paymentDue: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {t('billing.pdf.taxLabels', 'Tax & VAT Labels')}
              </Typography>
              
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('billing.pdf.taxColumnLabel', 'Tax Column Label')}
                    value={pdfSettings.taxLabelText}
                    onChange={(e) => setPdfSettings(prev => ({ ...prev, taxLabelText: e.target.value }))}
                  />
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('billing.pdf.sellerVatLabel', 'Seller VAT Label')}
                    value={pdfSettings.sellerVatLabel}
                    onChange={(e) => setPdfSettings(prev => ({ ...prev, sellerVatLabel: e.target.value }))}
                  />
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('billing.pdf.buyerVatLabel', 'Buyer VAT Label')}
                    value={pdfSettings.buyerVatLabel}
                    onChange={(e) => setPdfSettings(prev => ({ ...prev, buyerVatLabel: e.target.value }))}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {previewUrl && (
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  {t('billing.pdf.preview', 'PDF Preview')}
                </Typography>
                <Box sx={{ height: 400, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <iframe
                    src={previewUrl}
                    width="100%"
                    height="100%"
                    title="PDF Preview"
                    style={{ border: 'none' }}
                  />
                </Box>
              </CardContent>
            </Card>
          )}
        </Stack>
      </DialogContent>
      
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button
          variant="outlined"
          startIcon={<PreviewIcon />}
          onClick={handlePreviewPDF}
          disabled={isGenerating || !invoiceId}
        >
          {t('billing.pdf.preview', 'Preview')}
        </Button>
        <Button
          variant="outlined"
          startIcon={<EmailIcon />}
          onClick={() => setEmailDialogOpen(true)}
          disabled={!invoiceId}
        >
          {t('billing.pdf.sendEmail', 'Send Email')}
        </Button>
        <Button
          variant="contained"
          startIcon={isGenerating ? <CircularProgress size={20} /> : <DownloadIcon />}
          onClick={handleGeneratePDF}
          disabled={isGenerating || !invoiceId}
        >
          {isGenerating ? t('billing.pdf.generating', 'Generating...') : t('billing.pdf.download', 'Download PDF')}
        </Button>
      </DialogActions>

      <Dialog open={emailDialogOpen} onClose={() => setEmailDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('billing.pdf.sendInvoiceEmail', 'Send Invoice Email')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              {t('billing.pdf.emailInfo', 'The invoice PDF will be generated with your current settings and sent to the specified recipients.')}
            </Alert>
            <TextField
              fullWidth
              label={t('billing.pdf.emailRecipients', 'Email Recipients')}
              value={emailRecipients}
              onChange={(e) => setEmailRecipients(e.target.value)}
              placeholder="client@example.com, finance@example.com"
              helperText={t('billing.pdf.emailRecipientsHelp', 'Enter email addresses separated by commas')}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button 
            variant="contained" 
            onClick={handleSendEmail} 
            disabled={sendEmailMutation.isPending || !emailRecipients.trim()}
          >
            {sendEmailMutation.isPending ? t('common.sending', 'Sending...') : t('common.send', 'Send')}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default React.memo(InvoicePDFGenerator);
